import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { shouldOptionBeShown } from "../helpers/conditions.ts";
import {
  selectionsForActiveSteps,
  shouldUseEntry,
  shouldUseStep,
} from "../helpers/steps.ts";
import { ComplexBundle, WordPressProduct } from "../types/Product.ts";
import { Vehicle } from "../types/Vehicle";
import { useBooking } from "./BookingProvider";
import { ConfigurationContext } from "./ConfigurationContext.tsx";
import {
  OptionDetail,
  Pricing,
  ValidationError,
  VariantGroup,
  VehicleSelectionContext,
  VehicleSelectionContextProps,
} from "./VehicleSelectionContext.tsx";

interface VehicleSelectionProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages vehicle selection state and logic
 *
 * @TODO: This file is an abomination
 */
export const VehicleSelectionProvider: React.FC<
  VehicleSelectionProviderProps
> = ({ children }: VehicleSelectionProviderProps) => {
  const { vehicles, gvmUpgrades } = useBooking();
  const initial = useContext(ConfigurationContext);
  const orderMode: "new" | "editing" = useMemo(
    () => (initial.orderId ? "editing" : "new"),
    [initial.orderId],
  );

  // State management
  const [selectedMakeModelId, setSelectedMakeModelId] = useState<number | null>(
    null,
  );
  const [selectedVariants, setSelectedVariants] = useState<Vehicle[]>([]);
  const [selectedGvmWeight, setSelectedGvmWeight] = useState<string | null>(
    null,
  );
  const [selectedGvmUpgrade, setSelectedGvmUpgrade] =
    useState<WordPressProduct | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, number[] | number | null>
  >({});
  const [selectedOptionDetails, setSelectedOptionDetails] = useState<
    Record<string, OptionDetail>
  >({});
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [notes, setNotes] = useState<string>("");

  // TODO: This is a copy of code in TopBar.tsx. Refactor to avoid duplication
  // Get unique make/model combinations
  const makeModelMap = useMemo(() => {
    const map = new Map<number, string>();

    const makes = vehicles.filter((vehicle) => vehicle.parent === 0);
    for (const make of makes) {
      for (const model of make.children) {
        const key = `${make.name} ${model.name}`;
        // This is flipped compared to TopBar.tsx
        map.set(model.id, key);
      }
    }

    return map;
  }, [vehicles]);

  useEffect(() => {
    if (initial.orderId) {
      return;
    }

    if (initial.model && makeModelMap.has(initial.model)) {
      setSelectedMakeModelId(Number(initial.model));
    }

    const variants: Vehicle[] = [];

    const series = vehicles.find((v) => v.id === Number(initial.series));
    if (series && series.parent === Number(initial.model)) {
      variants.push(series);
    }

    const variant = vehicles.find((v) => v.id === Number(initial.variant));
    if (variant && variant.parent === Number(initial.series)) {
      variants.push(variant);
    }

    setSelectedVariants(variants);
  }, [initial, makeModelMap, vehicles]);

  useEffect(() => {
    if (initial.orderId) {
      return;
    }

    // Change the URL when any of the vehicle selection state changes
    const query = new URLSearchParams(window.location.search);

    if (selectedMakeModelId) {
      query.set("model", selectedMakeModelId.toString());
    } else {
      query.delete("model");
    }

    if (selectedVariants.length > 0) {
      query.set("series", selectedVariants[0].id.toString());
    } else {
      query.delete("series");
    }

    if (selectedVariants.length > 1) {
      query.set("variant", selectedVariants[1].id.toString());
    } else {
      query.delete("variant");
    }

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${query.toString()}`,
    );
  }, [initial.orderId, selectedMakeModelId, selectedVariants]);

  /**
   * Finds a vehicle by its ID
   * @param {number} id - Vehicle ID to find
   * @returns {Vehicle | undefined} Found vehicle or undefined
   */
  const findVehicleById = useCallback(
    (id: number) => {
      return vehicles.find((v) => v.id === id);
    },
    [vehicles],
  );

  const availableVariants = useMemo(() => {
    if (!selectedMakeModelId) {
      return [];
    }

    const selectedVehicle = findVehicleById(selectedMakeModelId);

    if (!selectedVehicle) {
      return [];
    }

    const variants: VariantGroup[] = [];

    let parentsToCheck = [selectedMakeModelId];
    let depth = 0;

    while (true) {
      const children = vehicles.filter((vehicle) =>
        parentsToCheck.includes(vehicle.parent),
      );
      if (children.length === 0) {
        break;
      }

      variants.push({
        label: depth === 0 ? "Series" : "Variant",
        values: children,
      });

      if (selectedVariants[depth]) {
        parentsToCheck = [selectedVariants[depth].id];
      } else {
        break;
      }
      depth++;
    }

    return variants;
  }, [vehicles, selectedMakeModelId, selectedVariants, findVehicleById]);

  const selectedModel = useMemo(() => {
    if (!selectedMakeModelId) {
      return null;
    }
    return findVehicleById(selectedMakeModelId) || null;
  }, [findVehicleById, selectedMakeModelId]);

  /**
   * Handles make/model selection change
   * @param {string | number} value - Selected make/model ID
   */
  const handleMakeModelChange = useCallback((value: number | null) => {
    setSelectedMakeModelId(value);
    setSelectedVariants([]);
  }, []);

  const handleVariantChange = useCallback(
    (index: number, value: number) => {
      const selectedVehicle = vehicles.find((v) => v.id === value) as Vehicle;

      setSelectedVariants((prev) => {
        const newVariants = prev.slice(0, index);
        if (selectedVehicle) {
          newVariants[index] = selectedVehicle;
        }
        return newVariants;
      });
    },
    [vehicles, setSelectedVariants],
  );

  const availableGvmUpgrades = useMemo(() => {
    if (!selectedMakeModelId) {
      return [];
    }

    let upgrades = gvmUpgrades;

    const ids = [selectedMakeModelId, ...selectedVariants.map((v) => v.id)];

    for (const id of ids) {
      upgrades = upgrades
        .filter((upgrade) =>
          upgrade.extensions.cc_complex_bundle.taxonomies[
            "vehicle_make_cat"
          ].some((t) => t.id === id),
        )
        .filter(
          (upgrade) =>
            upgrade.extensions.cc_complex_bundle.taxonomies["gvm_upgrade_cat"],
        );
    }

    const weights = Object.entries(
      upgrades.reduce(
        (acc, upgrade) => {
          const weight =
            upgrade.extensions.cc_complex_bundle.taxonomies[
              "gvm_upgrade_cat"
            ][0].name.match(/\d+/g)?.[0];
          if (!weight) {
            return acc;
          }
          if (!acc[weight]) {
            acc[weight] = [];
          }
          acc[weight].push(upgrade);

          return acc;
        },
        {} as Record<string, WordPressProduct[]>,
      ),
    );

    weights.sort((a, b) => Number(a[0]) - Number(b[0]));

    return weights;
  }, [gvmUpgrades, selectedVariants, selectedMakeModelId]);

  const handleGvmWeightChange = useCallback((weight: string | null) => {
    setSelectedGvmWeight(weight);
  }, []);

  const handleGvmUpgradeChange = useCallback(
    (id: number | null) => {
      if (!id) {
        setSelectedGvmUpgrade(null);
        return;
      }

      const upgrade = gvmUpgrades.find((u) => u.id === id);
      setSelectedGvmUpgrade(upgrade || null);
    },
    [gvmUpgrades],
  );

  // Derived state
  const model = vehicles.find((v) => v.id === selectedMakeModelId);
  const make = vehicles.find((v) => v.id === model?.parent);
  const selectedVehiclePrettyName =
    make && model ? make.name + " " + model.name : "";

  const steps = useMemo(() => {
    if (!selectedGvmUpgrade) {
      return [];
    }

    return selectedGvmUpgrade.extensions.cc_complex_bundle.steps;
  }, [selectedGvmUpgrade]);

  const activeSelectedOptions = useMemo(
    () => selectionsForActiveSteps(steps, selectedOptions, orderMode),
    [orderMode, selectedOptions, steps],
  );

  const productsByGroup = useMemo(() => {
    if (!selectedGvmUpgrade) {
      return [];
    }

    const productsByGroup: Record<
      string,
      {
        sort: number;
        step?: ComplexBundle.Step;
        entry?: ComplexBundle.Entry;
        options: ComplexBundle.Option[];
      }
    > = {};

    for (const [stepIndex, step] of steps.entries()) {
      if (!shouldUseStep(step, orderMode, activeSelectedOptions)) {
        continue;
      }
      for (const [entryIndex, entry] of step.entries.entries()) {
        if (!shouldUseEntry(entry, orderMode, activeSelectedOptions)) {
          continue;
        }
        const optionsToShow = Array.from(entry.products.entries()).filter(
          ([optionIndex, option]) => {
            const entryKey = `${stepIndex}-${entryIndex}`;

            if (!shouldOptionBeShown(option, activeSelectedOptions)) {
              return false;
            }

            if (entry.entry_type === "products-free-text-list") {
              return true;
            }

            if (entry.entry_type === "product-dropdown") {
              return activeSelectedOptions[entryKey] === optionIndex;
            }

            if (entry.entry_type === "products-free-text-checklist") {
              const selections = activeSelectedOptions[entryKey] as
                | number[]
                | undefined;
              return selections?.includes(optionIndex) ?? false;
            }

            return false;
          },
        );

        for (const [, option] of optionsToShow) {
          if (entry.entry_type === "product-dropdown") {
            const key = `${stepIndex}`;
            if (!productsByGroup[key]) {
              productsByGroup[key] = {
                sort: stepIndex * 1000,
                step,
                options: [],
              };
            }
            productsByGroup[key].options.push(option);
          } else {
            const key = `${stepIndex}-${entryIndex}`;
            if (!productsByGroup[key]) {
              productsByGroup[key] = {
                sort: stepIndex * 1000 + entryIndex,
                entry,
                options: [],
              };
            }
            productsByGroup[key].options.push(option);
          }
        }
      }
    }

    return Object.values(productsByGroup).sort((a, b) => a.sort - b.sort);
  }, [activeSelectedOptions, orderMode, selectedGvmUpgrade, steps]);

  const products = useMemo(() => {
    const result: ComplexBundle.Option[] = [];

    for (const group of productsByGroup) {
      if (group.options.length > 0) {
        result.push(...group.options);
      }
    }

    return result;
  }, [productsByGroup]);

  const handleOptionChange = useCallback(
    (
      stepIndex: number,
      entryIndex: number,
      optionIndex: number[] | number | null,
    ) => {
      const entryKey = `${stepIndex}-${entryIndex}`;
      setSelectedOptions((prev) => ({
        ...prev,
        [entryKey]: optionIndex,
      }));

      setValidationErrors((prev) =>
        prev.filter(
          (err) =>
            !(err.stepIndex === stepIndex && err.entryIndex === entryIndex),
        ),
      );
    },
    [],
  );

  useEffect(() => {
    // Don't select default options if we're editing an order since it might override actual user selections
    if (!selectedGvmUpgrade || orderMode === "editing") {
      return;
    }

    const steps = selectedGvmUpgrade.extensions.cc_complex_bundle.steps;

    // Iterate through all steps and entries to find the default selected options
    for (const [stepIndex, step] of steps.entries()) {
      if (!shouldUseStep(step, orderMode, activeSelectedOptions)) {
        continue;
      }
      for (const [entryIndex, entry] of step.entries.entries()) {
        if (!shouldUseEntry(entry, orderMode, activeSelectedOptions)) {
          continue;
        }
        for (const [optionIndex, option] of entry.products.entries()) {
          if (
            option.selected_on_load === "on" &&
            entry.entry_type !== "products-free-text-checklist"
          ) {
            // We ignore any dependencies here, because both the client and server
            // will ignore selected options that shouldn't be selectable anyway.

            // The only time this wouldn't work is if there were multiple options
            // in one entry with selected_on_load, but there are no instances
            // where this is the case, so we will simply declare that use case
            // to be unsupported.
            const entryKey = `${stepIndex}-${entryIndex}`;
            if (activeSelectedOptions[entryKey] === undefined) {
              handleOptionChange(stepIndex, entryIndex, optionIndex);
            }
          }
        }
      }
    }

    /*
     * Don't include activeSelectedOptions because although we use it, we don't want
     * to re-run the effect every time a selected option changes.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOptionChange, orderMode, selectedGvmUpgrade]);

  const pricing: Pricing = useMemo(() => {
    const total = products.reduce((acc, product) => {
      let quantity;

      if (product.quantity !== "") {
        quantity = Number(product.quantity);
      } else {
        quantity = selectedOptionDetails[product.key]?.quantity || 1;
      }

      if (product.product_option.adjusted_price) {
        return acc + Number(product.product_option.adjusted_price) * quantity;
      }
      return acc;
    }, 0.0);

    return {
      total,
    };
  }, [products, selectedOptionDetails]);

  const handleNotesChange = useCallback((value: string) => {
    setNotes(value);
  }, []);

  const addToCart: () => Promise<void> = useCallback(async () => {}, []);

  const handleOptionDetailChange: (
    optionKey: string,
    value: OptionDetail,
  ) => void = useCallback((optionKey, value) => {
    // Merge the new value with the existing one
    setSelectedOptionDetails((prev) => ({
      ...prev,
      [optionKey]: {
        ...prev[optionKey],
        ...value,
      },
    }));
  }, []);

  // const {
  //   data: order,
  //   // isLoading: isLoadingOrder,
  //   // error: orderError,
  // } = useGetOrderQuery(initial.orderId ?? skipToken);

  // useEffect(() => {
  //   const bundleData = order?.items?.[0]?.item_data?.bundle_data?.[0];
  //   if (!bundleData) {
  //     return;
  //   }
  //
  //   const product = availableGvmUpgrades;
  //   console.log(product);
  //
  //   console.log("bundleData", bundleData);
  // }, [order, availableGvmUpgrades]);

  const value: VehicleSelectionContextProps = {
    selectedMakeModelId,
    selectedModel,
    selectedImage: null,
    selectedVehiclePrettyName,
    handleMakeModelChange,
    availableVariants,
    selectedVariants,
    handleVariantChange,
    // No availableGvmWeights is needed as availableGvmUpgrades gives you that information
    selectedGvmWeight,
    handleGvmWeightChange,
    allGvmUpgrades: gvmUpgrades,
    availableGvmUpgrades,
    selectedGvmUpgrade,
    handleGvmUpgradeChange,
    selectedOptionDetails,
    handleOptionDetailChange,
    steps,
    products,
    productsByGroup,
    pricing,
    selectedOptions: activeSelectedOptions,
    handleOptionChange,
    addToCart,
    orderMode,
    validationErrors,
    setValidationErrors,
    notes,
    handleNotesChange,
  };

  return (
    <VehicleSelectionContext value={value}>{children}</VehicleSelectionContext>
  );
};
